import { z } from 'zod'
import { logger } from './logger'
import { recordDrift } from './drift-log'

/**
 * Schema-drift-tolerant wrapper for cosmetic Zod enums.
 *
 * Wraps `schema.catch((ctx) => ...)` to do two things at once:
 *   1. Replace an unknown / invalid value with `fallback` so the parent
 *      `parse()` succeeds — the field's parent document no longer drops
 *      out of the UI because of one bad string.
 *   2. Log a warning via the shared logger with the offending value + the
 *      field hint, so Sentry still hears about drift. Without the log,
 *      future seeder bugs or schema evolutions would silently coerce
 *      and we'd never know.
 *
 * Use this on **cosmetic** enum fields inside document schemas (parsing
 * Firestore reads) — `payment.method`, `booking.bookingType`, etc.
 * Do NOT use on structural fields where the value drives logic that
 * can't recover from a wrong default (IDs, dates, key statuses). Those
 * should stay strict — a parse failure there is a signal that the doc
 * isn't safely renderable anyway.
 *
 * Forms + callable inputs should keep using the bare `*Schema` export —
 * we want strict validation on the write side. Lenient applies on read
 * only, where Postel's law ("be conservative in what you send, liberal
 * in what you accept") protects us from drift in our own database.
 *
 * The fallback type is inferred from the schema, so the call site can't
 * accidentally pass a value that isn't in the enum.
 *
 * @example
 *   method: lenient(PaymentMethodSchema, 'other', 'payment.method'),
 */
export function lenient<T extends z.ZodEnum<[string, ...string[]]>>(
  schema: T,
  fallback: z.infer<T>,
  hint: string,
): z.ZodCatch<T> {
  return schema.catch((ctx) => {
    logger.w(
      `schema drift in ${hint}: got ${JSON.stringify(ctx.input)} → fallback ${JSON.stringify(fallback)}`,
    )
    recordDrift(hint, ctx.input, fallback)
    return fallback
  })
}

/**
 * Same as `lenient`, but for `.nullable().optional()` enum fields where
 * the natural fallback is `null` (e.g. `customerRole`, `payment.refundType`,
 * `crmPlayer.dominantHand`). Logs drift on unknown values; missing or
 * explicit-null values pass through normally without a log.
 */
export function lenientNullable<T extends z.ZodEnum<[string, ...string[]]>>(
  schema: T,
  hint: string,
): z.ZodCatch<z.ZodOptional<z.ZodNullable<T>>> {
  return schema
    .nullable()
    .optional()
    .catch((ctx) => {
      // Skip the drift log when the offending value is literally null /
      // undefined — that's the legitimate "no value" case the schema is
      // already meant to accept, not drift.
      if (ctx.input == null) return null
      logger.w(
        `schema drift in ${hint}: got ${JSON.stringify(ctx.input)} → fallback null`,
      )
      recordDrift(hint, ctx.input, null)
      return null
    })
}
